import * as React from 'react'
import { theme } from '../theme'
import { type ImageFileType } from './useAiGeneration'
import { UploadIcon } from './UploadIcon'

const allowedFileTypes: ImageFileType[] = ['image/jpeg', 'image/jpg', 'image/png']

interface Props {
    onImageSelect: (file: File) => void
    selectedImage: File | null
    error?: string | null
}

export const ImageUploadInput = ({
    onImageSelect,
    selectedImage,
    error
}: Props) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = React.useState(false)
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)

    // Create preview URL when image is selected
    React.useEffect(() => {
        if (selectedImage) {
            const url = URL.createObjectURL(selectedImage)
            setPreviewUrl(url)
            return () => URL.revokeObjectURL(url)
        }
        return undefined
    }, [selectedImage])

    const validateAndSelectFile = (file: File) => {
        if (!allowedFileTypes.includes(file.type as ImageFileType)) {
            return
        }
        onImageSelect(file)
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            validateAndSelectFile(file)
        }
    }

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault()
        if (!isDragging) setIsDragging(true)
    }

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault()
        setIsDragging(false)
        const file = event.dataTransfer.files?.[0]
        if (file) {
            validateAndSelectFile(file)
        }
    }

    const dropZoneStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        padding: '16px',
        backgroundColor: isDragging ? 'rgba(47, 123, 118, 0.1)' : theme.colors.background,
        border: error
            ? `2px solid ${theme.colors.error || '#dc2626'}`
            : isDragging
                ? `2px solid ${theme.colors.primary}`
                : `2px dashed ${theme.colors.border || '#E5E7EB'}`,
        borderRadius: theme.borderRadius.sm,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border 0.2s ease, box-shadow 0.2s ease',
        minHeight: '140px',
        position: 'relative',
        overflow: 'hidden',
    }

    const previewStyle: React.CSSProperties = previewUrl ? {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    } : {}

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.sm,
            }}>
            <label
                style={{
                    margin: 0,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    fontWeight: theme.typography.weights.medium,
                }}>
                Upload an image (click or drag & drop)
            </label>
            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={dropZoneStyle}>
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt="Preview"
                        style={previewStyle}
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                    }}>
                        <UploadIcon />
                        <div
                            style={{
                                fontSize: '12px',
                                color: theme.colors.text.primary,
                                textAlign: 'center',
                                fontWeight: 500,
                            }}>
                            Click to upload or drag & drop
                        </div>
                        <div
                            style={{
                                fontSize: '11px',
                                color: theme.colors.text.secondary,
                                textAlign: 'center',
                            }}>
                            JPG, JPEG or PNG only
                        </div>
                        {error && (
                            <div
                                style={{
                                    fontSize: '12px',
                                    color: theme.colors.error || '#dc2626',
                                    textAlign: 'center',
                                    marginTop: theme.spacing.xs,
                                }}>
                                {error}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    )
}
